import { Menu, BookOpenIcon, MessageCircle, X, Lightbulb } from 'lucide-react'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@components/ui/resizable"

import Breadcrumbs from '@components/Breadcrumbs'
import NavTabs from '@components/NavTabs/NavTabs'
import { useBreakpoint } from '@contexts/breakpoints'
import { Routes, Route } from 'react-router-dom'

const LessonOutlineSkeleton = () => (
  <div className='px-5 h-full min-h-full overflow-y-scroll'>
    <div className='sticky top-0 py-3 bg-background'>
      <div className='w-48 h-5 bg-accent rounded-sm animate-pulse mt-2 mb-3' />

      <div className='mb-4'>
        <div className='h-2 bg-accent rounded-sm animate-pulse' />
      </div>
    </div>

    <div className="pb-8 space-y-2">
      {[...Array(5)].map((_, idx) => (
        <div key={idx} className='flex flex-row items-center gap-2 w-full p-2 rounded-md mb-2'>
          <div className='flex items-center justify-center rounded-md shrink-0 p-2 w-10 h-10 bg-accent animate-pulse' />
          <div className='flex-grow'>
            <div className={`h-3 bg-accent rounded-sm animate-pulse ${idx % 2 === 0 ? 'w-3/4' : 'w-5/6'}`} />
          </div>
        </div>
      ))}
    </div>
  </div>
)

const ProjectBlocksSkeleton = () => (
  <div className='p-6 h-full overflow-y-auto'>
    <div className='space-y-6'>
      <div className='w-3/4 h-6 bg-accent rounded-sm animate-pulse' />

      <div className='space-y-4'>
        <div className='h-4 bg-accent w-full rounded-sm animate-pulse' />
        <div className='h-4 bg-accent w-5/6 rounded-sm animate-pulse' />
        <div className='h-4 bg-accent w-4/6 rounded-sm animate-pulse' />
      </div>

      <div className='space-y-3 mt-8'>
        {[...Array(4)].map((_, idx) => (
          <div key={idx} className='h-12 w-full bg-accent rounded-lg animate-pulse' />
        ))}
      </div>

      <div className='flex justify-between mt-8'>
        <div className='w-24 h-10 bg-accent rounded-md animate-pulse' />
        <div className='w-24 h-10 bg-accent rounded-md animate-pulse' />
      </div>
    </div>
  </div>
)

const ChatPlaygroundSkeleton = () => (
  <div className='h-full flex flex-col p-4'>
    <div className='flex-1 space-y-4 overflow-y-auto mb-4'>
      <div className='flex justify-end'>
        <div className='w-2/3 h-16 bg-accent rounded-lg animate-pulse' />
      </div>
      <div className='flex justify-start'>
        <div className='w-3/4 h-20 bg-accent rounded-lg animate-pulse' />
      </div>
      <div className='flex justify-end'>
        <div className='w-1/2 h-12 bg-accent rounded-lg animate-pulse' />
      </div>
      <div className='flex justify-start'>
        <div className='w-2/3 h-24 bg-accent rounded-lg animate-pulse' />
      </div>
    </div>

    <div className='border-t border-border pt-4'>
      <div className='h-12 bg-accent rounded-lg animate-pulse' />
    </div>
  </div>
)

const Skeleton = () => {
  const isMobile = useBreakpoint('sm')

  return (
    <div className='h-full flex flex-col max-h-full'>
      <Breadcrumbs className='overflow-x-auto flex items-center justify-between w-full'>
        <Breadcrumbs.Link label={<div className='w-24 h-4 bg-accent rounded-sm animate-pulse' />} />
        <Breadcrumbs.Text label={<div className='w-32 h-4 bg-accent rounded-sm animate-pulse' />} />

        <div className='flex items-center gap-3 hidden sm:flex ml-auto'>
          <div className='w-32 h-8 bg-accent rounded-md animate-pulse' />
          <div className='w-24 h-8 bg-accent rounded-md animate-pulse' />
          <div className='w-16 h-6 bg-accent rounded-md animate-pulse' />
        </div>
      </Breadcrumbs>

      <Choose>
        <When condition={isMobile}>
          <div className='h-[calc(100%-64px)] flex flex-col'>
            <NavTabs className='sticky top-0'>
              <NavTabs.Link
                to=''
                end
                icon={<Menu size={16} />}
                label='outline'
              />
              <NavTabs.Link
                to='lesson'
                end
                icon={<BookOpenIcon size={16} />}
                label='Lesson'
              />
              <NavTabs.Link
                to='chat'
                end
                icon={<MessageCircle size={16} />}
                label='Chat'
              />
            </NavTabs>

            <div className='h-[calc(100%-40px)] overflow-auto'>
              <Routes>
                <Route
                  path=""
                  element={<LessonOutlineSkeleton />}
                />
                <Route
                  path="lesson"
                  element={
                    <div className='pb-4 flex flex-col h-full overflow-y-auto'>
                      <ProjectBlocksSkeleton />
                      <div className='absolute bottom-0 left-0 w-full h-2 bg-accent animate-pulse' />
                    </div>
                  }
                />
                <Route
                  path="chat"
                  element={<ChatPlaygroundSkeleton />}
                />
              </Routes>
            </div>
          </div>
        </When>

        <Otherwise>
          <ResizablePanelGroup
            className='flex grow bg-background max-h-[calc(100vh-55px)]'
            style={{ overflow: 'auto' }}
            direction='horizontal'
            id='studentProjectPlayground'
          >
            <ResizablePanel defaultSize={20} minSize={15} maxSize={30} className='flex flex-col bg-background max-h-[calc(100vh-60px)] overflow-y-scroll'>
              <LessonOutlineSkeleton />
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={50} className='flex flex-col max-h-full'>
              <div className='h-full'>
                <ProjectBlocksSkeleton />
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={30} minSize={25} className='relative overflow-auto'>
              <div className="absolute top-2 right-2 z-20">
                <div className="p-2 rounded-sm bg-background w-8 h-8 animate-pulse" />
              </div>
              <ChatPlaygroundSkeleton />
            </ResizablePanel>
          </ResizablePanelGroup>
        </Otherwise>
      </Choose>
    </div>
  )
}

export default Skeleton
